import { HUB_CORNER_RADIUS_DEFAULT } from '../hub-corner-radius';
import { DEFAULT_SPACE_DENSITY, type SpaceDensity } from '../hub-space-density/types';
import { DEFAULT_ACCENT, DEFAULT_PRIMARY } from '../hub-theming-defaults';
import type { HubTypography } from '../hub-typography';

// Product defaults for the "customized vs default" comparison are imported from
// each dimension's owning module (single source of truth): brand colours from
// `hub-theming-defaults`, corner radius from `hub-corner-radius`, and space
// density from `hub-space-density` — so day-0 hubs can't be mis-flagged by a
// stale local copy. Still pure + framework-free (all four are plain constants).

export type HubThemingStateAttributes = {
	accentColor?: string;
	bodyFont?: string;
	colorsCustomized: boolean;
	cornerRadius?: number;
	cornerRadiusCustomized: boolean;
	customizedDimensionCount: number;
	headingFont?: string;
	primaryColor?: string;
	spaceDensity?: SpaceDensity;
	spaceDensityCustomized: boolean;
	typographyCustomized: boolean;
};

/**
 * Compute the "customized-vs-default" flags + chosen values for the four
 * advanced-theming dimensions. Pure + framework-free so it can be unit tested
 * without rendering and reused by both the Publish event and the render event.
 */
export const buildHubThemingStateAttributes = ({
	primaryColor,
	accentColor,
	typography,
	cornerRadius,
	spaceDensity,
}: {
	accentColor?: string;
	cornerRadius?: number;
	primaryColor?: string;
	spaceDensity?: SpaceDensity;
	typography?: HubTypography;
}): HubThemingStateAttributes => {
	const colorsCustomized =
		(!!primaryColor && primaryColor.toUpperCase() !== DEFAULT_PRIMARY) ||
		(!!accentColor && accentColor.toUpperCase() !== DEFAULT_ACCENT);
	const typographyCustomized = !!typography;
	const cornerRadiusCustomized =
		typeof cornerRadius === 'number' && cornerRadius !== HUB_CORNER_RADIUS_DEFAULT;
	const spaceDensityCustomized = !!spaceDensity && spaceDensity !== DEFAULT_SPACE_DENSITY;

	const customizedDimensionCount =
		Number(colorsCustomized) +
		Number(typographyCustomized) +
		Number(cornerRadiusCustomized) +
		Number(spaceDensityCustomized);

	return {
		customizedDimensionCount,
		colorsCustomized,
		typographyCustomized,
		cornerRadiusCustomized,
		spaceDensityCustomized,
		primaryColor,
		accentColor,
		bodyFont: typography?.body,
		headingFont: typography?.heading,
		cornerRadius,
		spaceDensity,
	};
};
